/*
 * Delivery-estimate banner (PDP).
 *
 * Rewritten 2026-08-21. Five defects were fixed - two found in the original audit
 * item and three more found in adversarial review of the first attempt. Behaviour is
 * otherwise unchanged: same 4 business days of transit, same anchor, same markup and
 * styling, and the same rendered date today.
 *
 * 1. THE CALENDAR EXPIRED. US_HOLIDAYS held ten hardcoded 2025 dates, so from
 *    1 Jan 2026 every US federal holiday was treated as a shipping day - Labor Day,
 *    Thanksgiving and Christmas included. Holidays are now COMPUTED from the rules
 *    that define them, so the calendar cannot expire again.
 *
 * 2. EVERY HOLIDAY LANDED A DAY EARLY. `new Date("2025-09-01")` parses an ISO
 *    date-only string as UTC midnight, and `.toDateString()` renders it in local
 *    time. In any negative-offset timezone - which is every US and Canadian
 *    shopper - that is the PREVIOUS day. Labor Day 2025 was matched as 31 Aug and
 *    1 Sep was treated as a shipping day. Dates are now built with
 *    `new Date(y, m - 1, d)`, which is local by construction.
 *
 * The 2026 calendar was validated by computing 2025 and comparing against the ten
 * dates this file used to carry: all ten reproduce exactly. The computed set adds
 * Juneteenth (19 Jun), which the hardcoded list had omitted.
 *
 * Market-aware: the shipping calendar follows the buyer's country, injected from
 * Liquid in sections/product.liquid. US and CA have different statutory holidays -
 * Canada Day, Victoria Day, Boxing Day and Good Friday are not US holidays, and
 * Thanksgiving falls in a different month.
 *
 * The banner string is NOT hardcoded here - it comes from the locale files via the
 * same injected config. (The rule requiring that lives in the WRAPPER around this
 * repo, at .cursor/rules/00-global.mdc:138, not in the theme itself.)
 */
function initDeliveryBanner() {
  var CONFIG = window.urestaDeliveryBanner || {};
  var COUNTRY = String(CONFIG.country || 'US').toUpperCase();
  var TEXT_TEMPLATE = CONFIG.text || '';
  var TRANSIT_BUSINESS_DAYS = 4;

  // Shopify's `t` filter returns a truthy "Translation missing: ..." literal for an
  // absent key, so a plain falsy check is not enough to keep it off the storefront.
  if (!TEXT_TEMPLATE || /translation missing/i.test(TEXT_TEMPLATE)) return;

  const getBannerAnchor = (fallbackTarget) => {
    const upsellContainer = document.querySelector('.complete-look');
    if (upsellContainer) {
      return { target: upsellContainer, position: 'beforebegin' };
    }
    return { target: fallbackTarget, position: 'afterend' };
  };

  /* ---------- holiday calculation ---------- */

  // Local-midnight constructor. `month` is 1-based. Day/month overflow is handled
  // by Date itself, which is what makes the observed() offsets safe at year edges.
  const localDate = (y, month, day) => new Date(y, month - 1, day);

  // The nth given weekday of a month, e.g. nthWeekday(2026, 9, 1, 1) = first Monday of September.
  const nthWeekday = (y, month, weekday, n) => {
    const first = localDate(y, month, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    return localDate(y, month, 1 + offset + (n - 1) * 7);
  };

  // The last given weekday of a month, e.g. Memorial Day.
  const lastWeekday = (y, month, weekday) => {
    const last = localDate(y, month + 1, 0);
    return localDate(y, month, last.getDate() - ((last.getDay() - weekday + 7) % 7));
  };

  // Fixed-date holidays move when they fall at a weekend: Sat -> Fri, Sun -> Mon.
  const observed = (d) => {
    if (d.getDay() === 6) return localDate(d.getFullYear(), d.getMonth() + 1, d.getDate() - 1);
    if (d.getDay() === 0) return localDate(d.getFullYear(), d.getMonth() + 1, d.getDate() + 1);
    return d;
  };

  const plusDays = (d, n) => localDate(d.getFullYear(), d.getMonth() + 1, d.getDate() + n);

  // Canada does NOT use the US Sat->Fri rule: a statutory holiday falling at a
  // weekend is observed on the following Monday, never the preceding Friday.
  const observedForward = (d) => {
    if (d.getDay() === 6) return plusDays(d, 2);   // Sat -> Mon
    if (d.getDay() === 0) return plusDays(d, 1);   // Sun -> Mon
    return d;
  };

  // Christmas and Boxing Day are consecutive, so when both need moving they cascade:
  // Christmas Sat 25 Dec 2027 -> Mon 27, Boxing Day Sun 26 -> Tue 28, not both to Mon.
  // Without this, the two collapse onto one date and the day they should have taken
  // is treated as shippable.
  // NOTE: this SORTS, so the returned order no longer matches the labelled order of
  // the array literal below. Read the labels as naming the inputs, not the outputs.
  const deconflict = (dates) => {
    const taken = {};
    return dates.slice().sort((a, b) => a - b).map((d) => {
      let x = new Date(d);
      while (x.getDay() === 0 || x.getDay() === 6 || taken[x.toDateString()]) x = plusDays(x, 1);
      taken[x.toDateString()] = true;
      return x;
    });
  };

  // Anonymous Gregorian algorithm. Needed for Good Friday, which is a Canadian
  // statutory holiday and moves every year.
  const easterSunday = (y) => {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    return localDate(y, month, ((h + l - 7 * m + 114) % 31) + 1);
  };

  // Victoria Day is the Monday PRECEDING 25 May - not simply the last Monday of May.
  // They differ in 2026: last Monday is 25 May, Victoria Day is 18 May.
  const victoriaDay = (y) => {
    const may25 = localDate(y, 5, 25);
    return localDate(y, 5, 25 - (((may25.getDay() + 6) % 7) || 7));
  };

  const holidaysFor = (y) => {
    if (COUNTRY === 'CA') {
      return deconflict([
        observedForward(localDate(y, 1, 1)),      // New Year's Day
        plusDays(easterSunday(y), -2),            // Good Friday
        victoriaDay(y),                           // Victoria Day
        observedForward(localDate(y, 7, 1)),      // Canada Day
        nthWeekday(y, 9, 1, 1),                   // Labour Day
        observedForward(localDate(y, 9, 30)),     // National Day for Truth and Reconciliation
        nthWeekday(y, 10, 1, 2),                  // Thanksgiving (CA)
        observedForward(localDate(y, 11, 11)),    // Remembrance Day
        observedForward(localDate(y, 12, 25)),    // Christmas Day
        observedForward(localDate(y, 12, 26)),    // Boxing Day
      ]);
    }
    return [
      observed(localDate(y, 1, 1)),        // New Year's Day
      nthWeekday(y, 1, 1, 3),              // Martin Luther King Jr Day
      nthWeekday(y, 2, 1, 3),              // Washington's Birthday
      lastWeekday(y, 5, 1),                // Memorial Day
      observed(localDate(y, 6, 19)),       // Juneteenth
      observed(localDate(y, 7, 4)),        // Independence Day
      nthWeekday(y, 9, 1, 1),              // Labor Day
      nthWeekday(y, 10, 1, 2),             // Columbus Day
      observed(localDate(y, 11, 11)),      // Veterans Day
      nthWeekday(y, 11, 4, 4),             // Thanksgiving
      observed(localDate(y, 12, 25)),      // Christmas Day
    ];
  };

  // Cache per year. A delivery estimate can cross a year boundary, so both the
  // current and next year are needed.
  const holidayCache = {};
  const holidayKeys = (y) => {
    if (!holidayCache[y]) {
      holidayCache[y] = holidaysFor(y).map((d) => d.toDateString());
    }
    return holidayCache[y];
  };

  const isWeekend = date => date.getDay() === 0 || date.getDay() === 6;
  // A holiday can be OBSERVED into an adjacent year - 1 Jan 2028 is a Saturday and is
  // observed on Fri 31 Dec 2027, which is computed under 2028 but looked up under 2027.
  // Checking only the date's own year misses it and treats a closed day as shippable.
  const isHoliday = (date) => {
    const y = date.getFullYear();
    const k = date.toDateString();
    return holidayKeys(y - 1).indexOf(k) !== -1 ||
           holidayKeys(y).indexOf(k) !== -1 ||
           holidayKeys(y + 1).indexOf(k) !== -1;
  };
  const isBusinessDay = date => !isWeekend(date) && !isHoliday(date);

  const addOneDay = date => {
    const d = new Date(date);
    d.setDate(date.getDate() + 1);
    return d;
  };

  const getNextBusinessDay = date => {
    let d = new Date(date);
    while (!isBusinessDay(d)) d = addOneDay(d);
    return d;
  };

  const addBusinessDays = (startDate, amount) => {
    let d = new Date(startDate);
    let count = 0;
    while (count < amount) {
      d = addOneDay(d);
      if (isBusinessDay(d)) count++;
    }
    return d;
  };

  const getOrdinal = (n) => {
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    const last = n % 10;
    if (last === 1) return `${n}st`;
    if (last === 2) return `${n}nd`;
    if (last === 3) return `${n}rd`;
    return `${n}th`;
  };

  const now = new Date();
  let start = isBusinessDay(now) ? addOneDay(now) : getNextBusinessDay(now);

  const deliveryDate = addBusinessDays(start, TRANSIT_BUSINESS_DAYS);

  const monthName = deliveryDate.toLocaleString("en-US", { month: "long" }).toUpperCase();
  const day = deliveryDate.getDate();
  const dayOrdinal = getOrdinal(day);
  const bannerText = TEXT_TEMPLATE.replace('__DATE__', `${monthName} ${dayOrdinal}`);

  const productForm = document.querySelector('product-form[id^="product-form-component"]');
  if (!productForm) return;

  const wrapper = productForm.parentElement;

  // Check if banner already exists
  const existingBanner = document.getElementById('uresta-delivery-banner');

  // If it exists, just update the text (in case the date changed)
  if (existingBanner) {
    const textElement = existingBanner.querySelector('.gb-delivery-banner__text');
    if (textElement) {
      textElement.textContent = bannerText;
    }
    const { target, position } = getBannerAnchor(wrapper);
    if (target) {
      target.insertAdjacentElement(position, existingBanner);
    }
    return;
  }

  const bar = document.createElement('div');
  bar.id = 'uresta-delivery-banner';
  bar.className = 'gb-delivery-banner';
  bar.style.background = '#d6ff7d';
  bar.style.display = 'flex';
  bar.style.justifyContent = 'center';
  bar.style.alignItems = 'center';
  bar.style.padding = '10px';
  bar.style.borderRadius = '6px';
  bar.style.marginTop = '27px';

  const text = document.createElement('p');
  text.className = 'gb-delivery-banner__text';
  text.textContent = bannerText;

  text.style.margin = '0';
  text.style.textAlign = 'center';
  text.style.color = '#014649';
  text.style.fontFamily = 'Poppins, sans-serif';
  text.style.fontSize = '14px';
  text.style.fontWeight = '600';

  bar.appendChild(text);
  const { target, position } = getBannerAnchor(wrapper);
  if (target) {
    target.insertAdjacentElement(position, bar);
  }
}

setTimeout(() => {
  initDeliveryBanner();
}, 100);

setTimeout(() => {
  initDeliveryBanner();
}, 900);

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initDeliveryBanner);
}
